import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Pause, Pencil, Play, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const EDITABLE_STATUSES = new Set(["pending", "approved", "paused", "rejected"]);
const PAUSABLE = new Set(["approved"]);
const RESUMABLE = new Set(["paused"]);
const DELETABLE = new Set(["pending", "approved", "paused", "rejected", "suspended"]);

function editPath(item) {
  const id = item.listing_id || item.vehicle_id || item.spot_id;
  if (item.type === "parking" || item.spot_id) {
    return `/parking/edit/${id}`;
  }
  return `/dashboard/owner/edit/${id}`;
}

function listingKey(item) {
  const type = item.type || (item.spot_id ? "parking" : "vehicle");
  const id = item.listing_id || item.vehicle_id || item.spot_id;
  return `${type}-${id}`;
}

export default function AdminListingActions({
  item,
  onPause,
  onResume,
  onDelete,
  compact = false,
  size,
}) {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!item) return null;

  const status = item.status || "unknown";
  const id = item.listing_id || item.vehicle_id || item.spot_id;
  const canPause = PAUSABLE.has(status);
  const canResume = RESUMABLE.has(status);
  const canDelete = DELETABLE.has(status);
  const canEdit = EDITABLE_STATUSES.has(status);
  const hasActions = canPause || canResume || canDelete || canEdit;
  if (!hasActions) return null;

  // Match the height/typography of the sibling Approve/Reject buttons at each mount point:
  // rows use h-9/text-xs (size="md"), review modals use h-10/text-sm (size="lg"). KAN-108.
  const btnSize =
    size === "lg"
      ? "px-4 h-10 text-sm"
      : size === "md"
        ? "px-3 h-9 text-xs"
        : compact
          ? "px-3 h-9 text-xs"
          : "px-3 h-8 text-xs";
  const btnClass = `${btnSize} zc-btn-ghost ${compact ? "flex" : "inline-flex"} items-center gap-1`;

  const runConfirm = async () => {
    setBusy(true);
    try {
      if (confirm === "paused") await onPause?.(item);
      else if (confirm === "approved") await onResume?.(item);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={`flex flex-wrap gap-2 ${compact ? "zc-stack-actions" : ""}`} data-testid={`admin-listing-actions-${listingKey(item)}`}>
        {canEdit && (
          <Link
            to={editPath(item)}
            className={`${btnClass} no-underline`}
            data-testid={`admin-edit-${id}`}
          >
            <Pencil size={12} /> Edit
          </Link>
        )}
        {canPause && (
          <button
            type="button"
            onClick={() => setConfirm("paused")}
            disabled={busy}
            className={btnClass}
            data-testid={`admin-pause-${id}`}
          >
            <Pause size={12} /> Pause
          </button>
        )}
        {canResume && (
          <button
            type="button"
            onClick={() => setConfirm("approved")}
            disabled={busy}
            className={`${btnClass} ${compact ? "zc-btn-primary" : ""}`}
            data-testid={`admin-resume-${id}`}
          >
            <Play size={12} /> Resume
          </button>
        )}
        {canDelete && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(item)}
            className={`${btnClass} text-[var(--zc-danger,#dc2626)]`}
            data-testid={`admin-delete-${id}`}
          >
            <Trash2 size={12} /> Delete
          </button>
        )}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="bg-white border border-[var(--zc-border)] max-w-md" data-testid="admin-listing-status-confirm">
          <DialogHeader>
            <DialogTitle className="zc-headline text-xl">
              {confirm === "paused" ? "Pause listing?" : "Resume listing?"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--zc-text-2)]">
            {confirm === "paused"
              ? "This listing will be hidden from search until resumed. Existing bookings are unaffected."
              : "This listing will appear in search again for new bookings. Existing bookings are unaffected."}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setConfirm(null)} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
            <button
              type="button"
              onClick={runConfirm}
              disabled={busy}
              className="px-4 h-10 text-sm rounded-md text-white disabled:opacity-50"
              style={{ background: "var(--zc-accent)" }}
              data-testid="admin-listing-status-submit"
            >
              {busy ? "Updating…" : confirm === "paused" ? "Pause listing" : "Resume listing"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
